import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { CommonState, GetReplyToCCAddresses, GetEmailNotificationSettings, SaveEmailNotificationSettings, ResetEmailNotificationSettings } from '@app/core/store';
import { ReplyToCCAddress, EmailNotificationSetting } from '@app/core/models/common';
import { CommonService } from '@app/core/services';

interface EmailSection {
  key: string;
  label: string;
  moduleId: number;
  formGroup: FormGroup;
  isEditing: boolean;
  savedValues?: any;
}

@Component({
  selector: 'app-email-settings',
  templateUrl: './email-settings.component.html',
  styleUrls: ['./email-settings.component.scss']
})
export class EmailSettingsComponent implements OnInit {
  @Select(CommonState.getReplyToCCAddresses)
  replyToCCAddresses$: Observable<ReplyToCCAddress[]>;

  @Select(CommonState.getEmailNotificationSettings)
  emailNotificationSettings$: Observable<EmailNotificationSetting[]>;

  dropdownList: any[] = [];
  replyToDropdownList: any[] = [];
  dropdownSettings!: IDropdownSettings;
  replyToDropdownSettings!: IDropdownSettings;
  sections: EmailSection[] = [];
  defaultEmail = '';
  defaultDisplayName = '';

  constructor(private fb: FormBuilder, private store: Store, private commonService: CommonService) {}

  ngOnInit() {
    this.store.dispatch(new GetReplyToCCAddresses());
    this.store.dispatch(new GetEmailNotificationSettings());

    this.replyToCCAddresses$.subscribe((addresses) => {
      if (addresses && addresses.length) {
        this.dropdownList = addresses.map((addr) => ({
          item_id: addr.id,
          item_text: addr.userName
        }));
        this.replyToDropdownList = addresses.map((addr) => ({
          item_id: addr.id,
          item_text: addr.userName
        }));
        this.patchSavedSettings();
      }
    });

    this.emailNotificationSettings$.subscribe((settings) => {
      if (settings && settings.length) {
        this.patchSavedSettings();
      }
    });

    this.dropdownSettings = {
      singleSelection: false,
      idField: 'item_id',
      textField: 'item_text',
      selectAllText: 'Select All',
      unSelectAllText: 'UnSelect All',
      itemsShowLimit: 2,
      allowSearchFilter: true
    };

    this.replyToDropdownSettings = {
      singleSelection: true,
      idField: 'item_id',
      textField: 'item_text',
      itemsShowLimit: 1,
      allowSearchFilter: true,
      closeDropDownOnSelection: true
    };

    const sectionModules = [
      { label: 'Payroll', moduleId: 3 },
      { label: 'Bookkeeping', moduleId: 1 },
      { label: 'Accounts Production', moduleId: 2 },
      { label: 'Corporation Tax', moduleId: 4 },
      { label: 'Personal Tax', moduleId: 5 },
      // { label: 'Accountant', moduleId: 6 },
      { label: 'Practice Management', moduleId: 7 },
      // { label: 'Payroll Advanced', moduleId: 8 },
      // { label: 'Company Secretary', moduleId: 9 },
      // { label: 'P11D', moduleId: 10 },
      { label: 'Trust Tax', moduleId: 11 },
      { label: 'Partnership Tax', moduleId: 12 },
      // { label: 'BookKeeping MTD', moduleId: 13 },
      // { label: 'Capium Hub', moduleId: 14 },
      // { label: 'Capisign', moduleId: 18 },
      { label: 'Time and Fee', moduleId: 35 },
      { label: 'Charity', moduleId: 36 },
      { label: 'Bookkeeping New', moduleId: 37 },
      { label: 'Corporation Tax New', moduleId: 38 },
      { label: 'SAMTD', moduleId: 45 },
      { label: 'Client Portal', moduleId: 49 },
      { label: 'Capium 365', moduleId: 60 },
      // { label: 'Identity', moduleId: 61 },
      // { label: 'Subscription Portal', moduleId: 62 },
    ];

    this.sections = sectionModules.map((section) => {
      const formGroup = this.fb.group({
        displayName: [''],
        ccAddress: [[]],
        replyToAddress: [[]]
      });
      formGroup.disable();
      return { key: `section_${section.moduleId}`, label: section.label, moduleId: section.moduleId, formGroup, isEditing: false };
    });
  }

  editSection(section: EmailSection): void {
    section.savedValues = section.formGroup.getRawValue();
    section.formGroup.enable();
    section.isEditing = true;
  }

  saveSection(section: EmailSection): void {
    const formValue = section.formGroup.getRawValue();
    const payload = {
      moduleId: section.moduleId,
      displayName: formValue.displayName || '',
      ccAddress: formValue.ccAddress?.map((item: any) => item.item_text).join(',') || '',
      replyToAddress: formValue.replyToAddress?.map((item: any) => item.item_text).join(',') || ''
    };
    this.store.dispatch(new SaveEmailNotificationSettings(payload)).subscribe(() => {
      section.formGroup.disable();
      section.isEditing = false;
      this.store.dispatch(new GetEmailNotificationSettings());
      this.commonService.onSucess('Email notification settings saved successfully.');
    });
  }

  cancelSection(section: EmailSection): void {
    section.formGroup.reset(section.savedValues);
    section.formGroup.disable();
    section.isEditing = false;
  }

  resetSection(section: EmailSection): void {
    this.store.dispatch(new ResetEmailNotificationSettings(section.moduleId)).subscribe(() => {
      section.formGroup.reset({ displayName: '', ccAddress: [], replyToAddress: [] });
      section.formGroup.disable();
      section.isEditing = false;
      this.store.dispatch(new GetEmailNotificationSettings());
      this.commonService.onSucess('Email notification settings reset to default successfully.');
    });
  }

  onItemSelect(item: any) {
    console.log(item);
  }

  onSelectAll(items: any) {
    console.log(items);
  }

  private patchSavedSettings(): void {
    const settings = this.store.selectSnapshot(CommonState.getEmailNotificationSettings);
    if (!this.dropdownList.length) {
      return;
    }

    const superAccountantEntry = settings?.find((s) => s.moduleId === 0);
    if (superAccountantEntry) {
      this.defaultEmail = superAccountantEntry.replyToAddress || '';
      this.defaultDisplayName = superAccountantEntry.displayName || '';
    }

    this.sections.forEach((section) => {
      const saved = settings?.find((s) => s.moduleId === section.moduleId);
      if (saved) {
        const ccItems = this.mapCommaStringToDropdownItems(saved.ccAddress, this.dropdownList);
        const replyItems = this.mapCommaStringToDropdownItems(saved.replyToAddress, this.replyToDropdownList);

        section.formGroup.patchValue({
          displayName: saved.displayName || '',
          ccAddress: ccItems,
          replyToAddress: replyItems
        });
      } else {
        this.setDefaultForSection(section);
      }
    });
  }

  private setDefaultForSection(section: EmailSection): void {
    if (!this.dropdownList.length) {
      return;
    }
    if (this.defaultDisplayName) {
      section.formGroup.patchValue({
        displayName: this.defaultDisplayName
      });
    }
    if (this.defaultEmail) {
      const defaultItem = this.replyToDropdownList.find((item) => item.item_text === this.defaultEmail);
      if (defaultItem) {
        section.formGroup.patchValue({
          replyToAddress: [defaultItem]
        });
      }
    }
  }

  private mapCommaStringToDropdownItems(commaString: string, sourceList: any[]): any[] {
    if (!commaString) {
      return [];
    }
    const emails = commaString.split(',').map((e) => e.trim()).filter((e) => e);
    return sourceList.filter((item) => emails.includes(item.item_text));
  }
}
